import {
  isCompositeFilterDescriptor,
  type CompositeFilterDescriptor,
  type FilterDescriptor,
} from '@progress/kendo-data-query'

import type {
  AstNode,
  AstGroup,
  AstPredicate,
  KendoOp,
  Logic,
  ValueOrList,
} from '@cyborg-monorepo/hunter/matchboi'

export function toAst(input: CompositeFilterDescriptor): AstNode {
  return toAstGroup(input)
}

function toAstGroup(g: CompositeFilterDescriptor): AstGroup {
  const logic = (g.logic === 'or' ? 'or' : 'and') as Logic
  const children: AstNode[] = []
  for (const f of g.filters ?? []) {
    if (isCompositeFilterDescriptor(f)) children.push(toAstGroup(f))
    else children.push(toAstPredicate(f))
  }
  return { kind: 'group', logic, children }
}

function toAstPredicate(fd: FilterDescriptor): AstPredicate {
  const op = String(fd.operator) as KendoOp
  const allowed = new Set([
    'eq',
    'neq',
    'lt',
    'lte',
    'gt',
    'gte',
    'contains',
    'doesnotcontain',
    'startswith',
    'endswith',
    'isnull',
    'isnotnull',
    'isempty',
    'isnotempty',
  ])
  if (!fd.field || !allowed.has(op)) {
    throw new Error(`Invalid filter: field=${fd.field} op=${fd.operator}`)
  }

  // Multi-value support:
  // - if editor emits an array -> keep it
  // - otherwise, split comma/newline separated strings defensively
  const raw =
    op === 'isnull' ||
    op === 'isnotnull' ||
    op === 'isempty' ||
    op === 'isnotempty'
      ? undefined
      : maybeList(fd.value)

  return {
    kind: 'pred',
    field: String(fd.field),
    op,
    value: raw,
    valueType: inferType(raw),
  }
}

function maybeList(v: unknown): ValueOrList | undefined {
  if (v == null) return undefined
  if (Array.isArray(v)) return v as any[]
  if (v instanceof Date) return v

  // strings: split on commas/newlines if it looks like a list
  if (typeof v === 'string') {
    const looksList = v.includes(',') || v.includes('\n')
    if (looksList) {
      return v
        .split(/[\n,]/g)
        .map((s) => s.trim())
        .filter((s) => s.length > 0)
    }
  }
  return v as any
}

function inferType(
  v: ValueOrList | undefined,
): 'string' | 'number' | 'boolean' | 'date' {
  const sample = Array.isArray(v) ? v[0] : v
  if (sample === undefined || sample === null) return 'string'
  if (typeof sample === 'number') return 'number'
  if (typeof sample === 'boolean') return 'boolean'
  if (sample instanceof Date) return 'date'
  return 'string'
}

export function fromAst(
  ast: AstNode,
): CompositeFilterDescriptor | FilterDescriptor {
  return fromAstNode(ast)
}

function fromAstNode(
  node: AstNode,
): CompositeFilterDescriptor | FilterDescriptor {
  if (node.kind === 'group') {
    return fromAstGroup(node)
  } else {
    return fromAstPredicate(node)
  }
}

function fromAstGroup(group: AstGroup): CompositeFilterDescriptor {
  return {
    logic: group.logic,
    filters: group.children.map(fromAstNode),
  }
}

function fromAstPredicate(pred: AstPredicate): FilterDescriptor {
  return {
    field: pred.field,
    operator: pred.op,
    value: restoreValue(pred.op, pred.value),
  }
}

/**
 * Handles conversion of nullary operators (like "isnull", "isempty")
 * which don't require a value in the Kendo filter.
 */
function restoreValue(op: KendoOp, value: ValueOrList | undefined): any {
  const nullaryOps = new Set(['isnull', 'isnotnull', 'isempty', 'isnotempty'])

  if (nullaryOps.has(op)) {
    return undefined
  }

  return value
}
